import { DestroyRef, Injectable, InjectionToken, Injector, Signal, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { App } from '@capacitor/app';
import { Store } from '@ngxs/store';
import { filter } from 'rxjs';

import { environment } from '@env/environment';
import { IApplicationBehavior, IMinAppBuild } from '@shared/models/application-behavior-model';
import { kzDetectPlatform } from '@shared/services/platform-capability.service';
import { kzLocalStorage } from '@shared/services/backend/adapters/firebase/firestore-init';
import { isCfdDisplayUrl } from '@shared/services/cfd/cfd-lock.rule';

import {
  KZ_GATED_PLATFORMS,
  KzBuildVerdict,
  KzDesktopInstaller,
  desktopInstallerFrom,
  desktopManifestUrl,
  desktopOs,
  minAppBuildVerdict,
  readRememberedMinAppBuild,
  rememberMinAppBuild,
  requiredBuildFor,
  sanitizeMinAppBuild,
} from './min-app-build';

/** What `App.getInfo()` answers that the gate reads: `build` (native build number) and `version` (Electron's semver). */
export interface KzAppBuildInfo {
  readonly build?: string;
  readonly version?: string;
}

/** The running shell (`kzDetectPlatform()`: `android`, `ios`, `electron` or `web`) — a spec provides its own. */
export const APP_BUILD_PLATFORM = new InjectionToken<string>('APP_BUILD_PLATFORM', {
  providedIn: 'root',
  factory: () => kzDetectPlatform(),
});

/**
 * This build's identity: `@capacitor/app` `App.getInfo()`. Native: Android's `versionCode` / iOS's `CFBundleVersion`
 * in `build`. Electron: `@capawesome/capacitor-electron`'s built-in App bridge answers `app.getVersion()` (the
 * `electron/package.json` version) in both `build` and `version` — no bridge of our own is needed. A spec stubs it.
 */
export const APP_BUILD_INFO = new InjectionToken<() => Promise<KzAppBuildInfo>>('APP_BUILD_INFO', {
  providedIn: 'root',
  factory: () => () => App.getInfo(),
});

/** Where the gate remembers the last minimum read (`kzLocalStorage()`; `null` where blocked) — a spec provides its own. */
export const APP_BUILD_STORAGE = new InjectionToken<Storage | null>('APP_BUILD_STORAGE', {
  providedIn: 'root',
  factory: () => kzLocalStorage(),
});

/** Reads the desktop releases manifest (`fetch`, no cache) — a spec provides its own. */
export const APP_BUILD_FETCH_JSON = new InjectionToken<(url: string) => Promise<unknown>>('APP_BUILD_FETCH_JSON', {
  providedIn: 'root',
  factory: () => async (url: string) => {
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  },
});

/**
 * The address on screen, for the customer-display exemption — a spec provides its own. It starts from the document's
 * own address, so a window that OPENS on the display (the Electron customer-display window, the web popup, a locked
 * display's cold start) is exempt from its first frame, before the router's first navigation ends; then it follows
 * every navigation (`urlAfterRedirects`).
 */
export const APP_BUILD_CURRENT_URL = new InjectionToken<Signal<string>>('APP_BUILD_CURRENT_URL', {
  providedIn: 'root',
  factory: () => {
    const loc = globalThis.location;
    const url = signal(loc ? `${loc.pathname}${loc.search}` : '');
    inject(Router, { optional: true })
      ?.events.pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(inject(DestroyRef)),
      )
      .subscribe((e) => url.set(e.urlAfterRedirects));
    return url.asReadonly();
  },
});

/**
 * This build as the gate compares it: Android/iOS the native build number, Electron the app's semver; `null` on the
 * web (never gated, never asked), or when the plugin cannot answer (fail open). Never throws.
 */
export async function readThisAppBuild(platform: string, getInfo: () => Promise<KzAppBuildInfo>): Promise<string | null> {
  if (!KZ_GATED_PLATFORMS.has(platform)) {
    return null;
  }
  try {
    const info = await getInfo();
    const v = platform === 'electron' ? info?.version : info?.build;
    return typeof v === 'string' && v.trim() ? v.trim() : null;
  } catch {
    return null;
  }
}

/**
 * The minimum-version gate (kzd-ac AC-R6, `docs/kz-appcheck/PLAN-appcheck.md` §5 step 5, §6 "Old builds"): Firestore
 * enforcement of App Check would refuse every write of a register still on a build without App Check, in silence. This
 * tells such a device to update first.
 *
 * - The minimum is `minAppBuild` on `app-behavior-state` (`IMinAppBuild`), in the base state every start reads — signed
 *   out too (the sign-in page is gated as well). Each time the base state lands (this device's copy, then the live
 *   document) the minimum it carries is taken and remembered (`kz.min-app-build.v1:<project>`), and a document without
 *   one forgets it. Offline, or before the base state answers, the remembered one applies: a device that saw the gate
 *   once stays gated offline.
 * - This build: `App.getInfo()` (`readThisAppBuild`).
 * - The decision: `minAppBuildVerdict` — fail open; the web is never gated (nothing is read there at all).
 *
 * Never on the customer display (lead ruling, 30 Oct): customers must never see "Update required". A display route
 * (`isCfdDisplayUrl`: `/provider/app/<pos|restaurant>/display` — the Electron customer-display window, the web popup,
 * a device locked to the display) keeps `blocked` false; the staff screen beside it shows the gate. `verdict` still
 * says `update` there.
 *
 * `blocked` only puts the "Update required" screen over the app (`KzUpdateRequiredComponent`). Nothing is stopped
 * underneath: the Firestore queue, the outbox and the offline sync keep sending — the screen stops new work only.
 *
 * Started once by an APP_INITIALIZER that never waits for it (`app.module.ts`).
 */
@Injectable({ providedIn: 'root' })
export class AppBuildGateService {
  readonly platform = inject(APP_BUILD_PLATFORM);
  private readonly getInfo = inject(APP_BUILD_INFO);
  private readonly storage = inject(APP_BUILD_STORAGE);
  private readonly fetchJson = inject(APP_BUILD_FETCH_JSON);
  private readonly currentUrl = inject(APP_BUILD_CURRENT_URL);
  /* The store is asked at `start()`, not when the service is built: the app root reads `blocked` (a spec of it has no NGXS). */
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly project = environment.firebase?.projectId ?? null;
  private started = false;

  /** This build (`null` until read, or unreadable). */
  readonly thisBuild = signal<string | null>(null);
  /** The minimum in force: the last one read, live or remembered (`null` = no gate). */
  readonly minAppBuild = signal<IMinAppBuild | null>(null);
  readonly verdict = computed<KzBuildVerdict>(() => minAppBuildVerdict(this.platform, this.thisBuild(), this.minAppBuild()));
  /** The "Update required" screen is up. */
  /** The screen shows the customer display (any module's): never gated. */
  readonly onCustomerDisplay = computed(() => isCfdDisplayUrl(this.currentUrl()));
  readonly blocked = computed(() => this.verdict() === 'update' && !this.onCustomerDisplay());
  /** The minimum this shell needs, for the screen ("Needed: 990"). */
  readonly required = computed(() => requiredBuildFor(this.platform, this.minAppBuild()));

  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    if (!KZ_GATED_PLATFORMS.has(this.platform)) {
      return; // the web: hosting always serves the latest build
    }
    this.minAppBuild.set(readRememberedMinAppBuild(this.project, this.storage));
    void readThisAppBuild(this.platform, this.getInfo).then((build) => this.thisBuild.set(build));
    this.injector
      .get(Store, null)
      ?.select((s: { app?: { appBehavior?: IApplicationBehavior | null } }) => s?.app?.appBehavior ?? null)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((behavior) => {
        if (behavior) {
          this.noteConfig(behavior.minAppBuild);
        }
      });
  }

  /**
   * The base state's `app-behavior-state` has landed: its `minAppBuild` is now the minimum, remembered for an offline
   * start. A document without one (or with only malformed fields) lifts the gate and forgets it — fail open.
   */
  noteConfig(raw: unknown): void {
    const min = sanitizeMinAppBuild(raw);
    this.minAppBuild.set(min);
    rememberMinAppBuild(this.project, min, this.storage);
  }

  /**
   * Electron: the installer to offer from the releases manifest (`desktopInstallerFrom`), for this computer and at or
   * above the minimum; `null` offline, on an unreadable manifest, or when none fits — the screen then only says to get
   * the new version. Never throws.
   */
  async desktopInstaller(userAgent: string | null = typeof navigator === 'undefined' ? null : navigator.userAgent): Promise<KzDesktopInstaller | null> {
    const url = desktopManifestUrl(this.project);
    if (this.platform !== 'electron' || !url) {
      return null;
    }
    try {
      return desktopInstallerFrom(await this.fetchJson(url), desktopOs(userAgent), this.minAppBuild()?.desktop);
    } catch {
      return null;
    }
  }
}
